/**
 * **A05 · The radio card** and **A07 · the gate clicker sheet** (Stream A, Paper.dc.html). The
 * radio card is laid out by the `field_papers` engine from the year's radio plan
 * (`GET /ops/radio-plan`, WORKLIST ops-new): channels and call signs on the front, the phrases and
 * the lightning rule on the back; what the plan does not hold prints NO DATA. The gate sheet is a
 * sheet per gate from Festival Ops's gates (`GET /ops/gates`), the hours and readings to write.
 */
import type { ReactNode } from 'react';
import { FestivalClock, gateSheets, radioCard, type RadioPlan } from '@cider/domain/engines';
import { useLoad } from '../views/money/shared.js';
import { usePrinted } from './printed.js';
import { DrawnPages } from './drawn-doc.js';

export function RadioCard({ year }: { year: string }): ReactNode {
  const plan = useLoad<RadioPlan>(`/ops/radio-plan?year=${year}`);
  const ready = plan.data !== null || plan.error !== null;
  usePrinted('radio-card', ready, { year: Number(year) });
  if (!ready) return <p>Reading the radio plan…</p>;
  return <DrawnPages pages={radioCard(Number(year), plan.data)} stamp="time" />;
}

/** One sheet per gate Festival Ops lists, for the day asked (today's operating day by default). */
export function GateSheet({ year, day }: { year: string; day?: string | undefined }): ReactNode {
  const gates = useLoad<{ rows: { gate: string }[] }>(`/ops/gates?year=${year}`);
  const ready = gates.data !== null || gates.error !== null;
  usePrinted('gate-sheet', ready, { year: Number(year) });
  if (!ready) return <p>Reading the gates…</p>;
  const names = gates.data ? [...new Set(gates.data.rows.map((g) => g.gate))] : null;
  const on = day ?? FestivalClock.operatingDay(new Date());
  return <DrawnPages pages={gateSheets(on, names)} stamp="time" />;
}
